const DEV_EVENTS_URL = 'https://dev.events/rss.xml';
const CACHE_KEY = 'dev_events_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

function getCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { timestamp, data } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCache(data) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        data,
      }),
    );
  } catch {
    // Ignore storage errors
  }
}

const CORS_PROXIES = [
  (url) => `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
  (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
  (url) => `https://proxy.cors.sh/${url}`,
  (url) => `https://cors-anywhere.herokuapp.com/${url}`,
  (url) => `https://crossorigin.me/${url}`,
  (url) => `https://yacdn.org/proxy/${url}`,
];

async function fetchWithProxy(url, proxyIndex = 0) {
  if (proxyIndex >= CORS_PROXIES.length) {
    throw new Error('All proxies failed');
  }

  try {
    const proxyUrl = CORS_PROXIES[proxyIndex](url);
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle allorigins response format
    if (proxyUrl.includes('allorigins.win/get')) {
      const json = await response.json();
      let { contents } = json;

      // Check if content is base64 encoded (data URI)
      if (contents.startsWith('data:')) {
        const base64Match = contents.match(/base64,(.+)/);
        if (base64Match) {
          // Decode base64 with proper UTF-8 support
          const binaryString = atob(base64Match[1]);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          contents = new TextDecoder('utf-8').decode(bytes);
        }
      }

      return contents;
    }

    return response.text();
  } catch (error) {
    console.warn(`Proxy ${proxyIndex + 1} failed, trying next...`);
    return fetchWithProxy(url, proxyIndex + 1);
  }
}

function parseRSSXML(xmlText) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'text/xml');

  // Check for parsing errors
  const parserError = xml.querySelector('parsererror');
  if (parserError) {
    return [];
  }

  const items = [];
  const entries = xml.querySelectorAll('item');

  entries.forEach((entry) => {
    const title = entry.querySelector('title')?.textContent || '';
    const link = entry.querySelector('link')?.textContent || '';
    const description = entry.querySelector('description')?.textContent || '';
    const pubDate = entry.querySelector('pubDate')?.textContent || '';

    // Get image from enclosure tag
    let image = '';
    const enclosure = entry.querySelector('enclosure');
    if (enclosure) {
      image = enclosure.getAttribute('url') || '';
    }

    // If no enclosure, try to extract image from description
    if (!image) {
      const imgMatch = description.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch) {
        [, image] = imgMatch;
      }
    }

    // Get categories
    const categories = [];
    entry.querySelectorAll('category').forEach((cat) => {
      categories.push(cat.textContent);
    });

    // Clean description from HTML tags
    const cleanDescription = description
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .trim()
      .slice(0, 300);

    // Parse date from description (format: "December 11-12, 2025" or similar)
    let eventDate = new Date();
    const dateMatch = description.match(/(\w+ \d+(?:-\d+)?, \d{4})/);
    if (dateMatch) {
      const parsedDate = new Date(dateMatch[1].replace(/-\d+/, ''));
      if (!Number.isNaN(parsedDate.getTime())) {
        eventDate = parsedDate;
      }
    } else if (pubDate) {
      eventDate = new Date(pubDate);
    }

    items.push({
      _id: `dev-events-${link}`,
      title,
      link,
      description: cleanDescription,
      image,
      categories,
      startDate: eventDate.toISOString().slice(0, 16),
      endDate: eventDate.toISOString().slice(0, 16),
      source: 'dev.events',
    });
  });

  return items;
}

export async function fetchDevEvents() {
  // Check cache first
  const cached = getCache();
  if (cached) {
    return cached;
  }

  try {
    const xmlText = await fetchWithProxy(DEV_EVENTS_URL);
    const items = parseRSSXML(xmlText);

    // Cache the results
    setCache(items);

    return items;
  } catch (error) {
    console.error('Error fetching dev.events:', error);
    return [];
  }
}

export default fetchDevEvents;
